import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import StripeService from "../services/StripeService.js";
import Spinner from "./Spinner";
import BookingReceipt, { Card, TicketList } from "./BookingReceipt";

const POLL_INTERVAL_MS = 2000;
const SLOW_AFTER_MS = 60000;

// Return page for redirect-based payments (Swish/Klarna). All state comes
// from the payment_intent query parameter, so the page survives reloads and
// shared links. Polls the backend until the booking exists.
const PaymentReturn = ({ paymentIntentId }) => {
    const [phase, setPhase] = useState('pending'); // pending | slow | ready | failed
    const [booking, setBooking] = useState(null);
    const [copied, setCopied] = useState(false);
    const history = useHistory();
    const doneRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        let intervalId = null;
        const startedAt = Date.now();

        const stop = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const start = () => {
            if (!intervalId && !doneRef.current) {
                intervalId = setInterval(poll, POLL_INTERVAL_MS);
            }
        };

        const waitingPhase = () => (Date.now() - startedAt > SLOW_AFTER_MS ? 'slow' : 'pending');

        const poll = async () => {
            try {
                const result = await StripeService.getBookingByIntent(paymentIntentId);
                if (cancelled) return;

                if (result.status === 'ready') {
                    setBooking(result.booking);
                    setPhase('ready');
                    doneRef.current = true;
                    stop();
                } else if (result.status === 'failed') {
                    setPhase('failed');
                    doneRef.current = true;
                    stop();
                } else {
                    setPhase(waitingPhase());
                }
            } catch (err) {
                if (cancelled) return;
                if (err.response && err.response.status === 404) {
                    setPhase('failed');
                    doneRef.current = true;
                    stop();
                } else {
                    // Temporary hiccup (network, 5xx, throttling): keep waiting.
                    setPhase(waitingPhase());
                }
            }
        };

        // Pause polling while the tab is hidden, resume on focus.
        const onVisibilityChange = () => {
            if (document.hidden) {
                stop();
            } else if (!doneRef.current) {
                poll();
                start();
            }
        };

        poll();
        start();
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            cancelled = true;
            stop();
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [paymentIntentId]);

    const copyReference = () => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(paymentIntentId)
                .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                })
                .catch(() => {});
        }
    };

    return (
        <Card>
            <div aria-live="polite">
                {phase === 'pending' && (
                    <div className="flex flex-col justify-center items-center py-8 text-center">
                        <Spinner />
                        <h3 className="mt-4 text-lg font-medium text-gray-900">Vi bekräftar din betalning</h3>
                        <p className="mt-2 text-sm text-gray-500 animate-pulse">Det tar oftast bara några sekunder.</p>
                    </div>
                )}

                {phase === 'slow' && (
                    <div className="flex flex-col justify-center items-center py-8 text-center">
                        <h3 className="text-lg font-medium text-gray-900">Din betalning är genomförd</h3>
                        <p className="mt-2 text-sm text-gray-500">Bekräftelsen kommer på mail inom kort.</p>
                        <div className="mt-6 flex items-center bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                            <span className="text-sm text-gray-700 font-mono">{paymentIntentId}</span>
                            <button
                                type="button"
                                onClick={copyReference}
                                className="ml-3 text-sm font-medium text-indigo-600 hover:text-indigo-500"
                            >
                                {copied ? 'Kopierat!' : 'Kopiera'}
                            </button>
                        </div>
                        <p className="mt-4 text-sm text-gray-500">
                            Har du inte fått något mail inom en timme? Kontakta kundtjänst och uppge referensnumret ovan.{' '}
                            <a href="/" className="font-medium text-indigo-600 hover:text-indigo-500">Till startsidan</a>
                        </p>
                    </div>
                )}

                {phase === 'failed' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                        <h3 className="text-lg font-medium text-red-800">Betalningen genomfördes inte</h3>
                        <p className="mt-2 text-sm text-red-700">
                            Inga biljetter har bokats. Du kan försöka igen – inga pengar dras för en avbruten betalning.
                        </p>
                        <button
                            onClick={() => history.push('/')}
                            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                            Tillbaka till startsidan
                        </button>
                    </div>
                )}

                {phase === 'ready' && booking && (
                    <div>
                        <BookingReceipt booking={booking} heading="Tack för din bokning!" />
                        <div className="mt-6 text-center">
                            <button
                                onClick={() => history.push('/mina-bokningar?bokningsnummer=' + (booking.bookingNo || ''))}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                            >
                                Mina bokningar
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
};

const Confirmation = () => {
    const tickets = useSelector(state => state.tickets);
    const location = useLocation();
    const history = useHistory();

    const paymentIntentId = new URLSearchParams(location.search).get('payment_intent');

    if (paymentIntentId) {
        return <PaymentReturn paymentIntentId={paymentIntentId} />;
    }

    // Legacy view for flows that land here without a payment reference
    // (e.g. bookings fully covered by a coupon): show tickets from the store.
    if (!tickets || !tickets.data || tickets.data.length === 0) {
        return (
            <Card>
                <div className="text-center py-8">
                    <h3 className="text-lg font-medium text-gray-900">Ingen bokning hittades</h3>
                    <p className="mt-2 text-sm text-gray-500">
                        Det finns ingen aktiv bokning att visa.
                    </p>
                    <button
                        onClick={() => history.push('/')}
                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                        Boka en resa
                    </button>
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <h3 className="text-center text-base font-semibold text-indigo-600 tracking-wide uppercase">Din bokning</h3>
            <h4 className="text-center text-base font-semibold text-indigo-600 tracking-wide uppercase">Resenärer</h4>
            <TicketList tickets={tickets.data} />
        </Card>
    );
};

export default Confirmation;
